import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from '../shared/models/content.models';
import type { EffectivePermissions } from '../platform/permissions/permission.types';

export type AuthUser = {
  userId: string;
  name: string;
  email: string;
  roleKey: string;
  avatarUrl?: string;
  permissions?: EffectivePermissions;
  hqTenant?: {
    id: string;
    franchiseId: string;
    name: string;
    code: string;
  };
};

export type TenantAuthUser = {
  userId: string;
  email: string;
  role: string;
  /** Business tenant identifier, e.g. blr-b7c3e1 */
  franchiseId: string;
  /** Mongo franchises._id (internal) */
  franchiseRef?: string;
  firstName?: string;
  lastName?: string;
  franchise?: {
    id: string;
    franchiseId?: string;
    name: string;
    code: string;
    isActive?: boolean;
  };
};

export type SessionUserType = 'platform' | 'tenant';

export type StoredSession = {
  userType: SessionUserType;
  user: AuthUser | TenantAuthUser;
};

type LoginData = {
  accessToken: string;
  userType: SessionUserType;
  redirectTo: string;
  user: AuthUser | TenantAuthUser;
};

type MeData = (AuthUser | TenantAuthUser) & {
  userType: SessionUserType;
  isActive?: boolean;
  isSystemUser?: boolean;
  lastLoginAt?: string;
  roleKey?: string;
  role?: string;
  franchiseId?: string;
  franchiseRef?: string;
  hqTenant?: AuthUser['hqTenant'];
  franchise?: TenantAuthUser['franchise'];
  permissions?: EffectivePermissions;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'access_token';
  private readonly sessionKey = 'current_session';
  private readonly apiUrl = environment.apiUrl;

  readonly session = signal<StoredSession | null>(this.readStoredSession());
  readonly loading = signal(false);

  readonly currentUser = signal<AuthUser | null>(this.readPlatformUser());
  readonly userType = signal<SessionUserType | null>(this.session()?.userType ?? null);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  async login(googleToken: string): Promise<{ redirectTo: string }> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.post<ApiEnvelope<LoginData>>(
          `${this.apiUrl}/auth/google`,
          { googleToken },
          { withCredentials: true },
        ),
      );
      const payload = response.data;
      this.setSession(payload.accessToken, payload.userType, payload.user);
      return { redirectTo: payload.redirectTo };
    } finally {
      this.loading.set(false);
    }
  }

  async refreshSession(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiEnvelope<{ accessToken: string; userType?: SessionUserType }>>(
          `${this.apiUrl}/auth/refresh`,
          {},
          { withCredentials: true },
        ),
      );
      this.setToken(response.data.accessToken);
      if (response.data.userType) {
        const current = this.session();
        if (current) {
          this.persistSession({ ...current, userType: response.data.userType });
        }
      }
      return true;
    } catch {
      this.clearSession();
      return false;
    }
  }

  async restoreSession(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) {
      return this.refreshSession();
    }

    try {
      const me = await firstValueFrom(
        this.http.get<ApiEnvelope<MeData>>(`${this.apiUrl}/auth/me`, { withCredentials: true }),
      );
      this.applyMePayload(me.data);
      return true;
    } catch {
      const refreshed = await this.refreshSession();
      if (!refreshed) return false;

      try {
        const me = await firstValueFrom(
          this.http.get<ApiEnvelope<MeData>>(`${this.apiUrl}/auth/me`, { withCredentials: true }),
        );
        this.applyMePayload(me.data);
        return true;
      } catch {
        this.clearSession();
        return false;
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true }));
    } catch {
      // ignore network errors on logout
    }

    this.clearSession();
    await this.router.navigateByUrl('/auth/login');
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser();
  }

  getTenantUser(): TenantAuthUser | null {
    const session = this.session();
    if (session?.userType === 'tenant') {
      return session.user as TenantAuthUser;
    }
    return null;
  }

  isPlatformSession(): boolean {
    return this.userType() === 'platform';
  }

  isTenantSession(): boolean {
    return this.userType() === 'tenant';
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getAccessToken(): string | null {
    if (!this.isBrowser()) return null;
    const token = localStorage.getItem(this.tokenKey);
    if (!token || token === 'undefined') return null;
    return token;
  }

  clearSession(): void {
    if (this.isBrowser()) {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.sessionKey);
    }
    this.session.set(null);
    this.currentUser.set(null);
    this.userType.set(null);
  }

  private setSession(
    accessToken: string,
    userType: SessionUserType,
    user: AuthUser | TenantAuthUser,
  ): void {
    if (userType === 'platform') {
      const platformUser = user as AuthUser;
      user = { ...platformUser, avatarUrl: this.normalizeAvatarUrl(platformUser.avatarUrl) };
    }
    this.setToken(accessToken);
    this.persistSession({ userType, user });
  }

  private normalizeAvatarUrl(url?: string | null): string | undefined {
    const trimmed = url?.trim();
    if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
      return undefined;
    }
    return trimmed;
  }

  private persistSession(session: StoredSession): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.sessionKey, JSON.stringify(session));
    }
    this.session.set(session);
    this.userType.set(session.userType);
    if (session.userType === 'platform') {
      this.currentUser.set(session.user as AuthUser);
    } else {
      this.currentUser.set(null);
    }
  }

  private setToken(accessToken: string): void {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.tokenKey, accessToken);
  }

  private applyMePayload(me: MeData): void {
    if (me.userType === 'platform') {
      const user: AuthUser = {
        userId: me.userId,
        name: (me as AuthUser).name ?? me.email,
        email: me.email,
        roleKey: me.roleKey ?? '',
        avatarUrl: this.normalizeAvatarUrl((me as AuthUser).avatarUrl),
        hqTenant: me.hqTenant,
        permissions: me.permissions,
      };
      this.persistSession({ userType: 'platform', user });
    } else {
      const user: TenantAuthUser = {
        userId: me.userId,
        email: me.email,
        role: me.role ?? '',
        franchiseId: me.franchiseId ?? me.franchise?.franchiseId ?? '',
        franchiseRef: me.franchiseRef ?? me.franchise?.id,
        firstName: (me as TenantAuthUser).firstName,
        lastName: (me as TenantAuthUser).lastName,
        franchise: me.franchise,
      };
      this.persistSession({ userType: 'tenant', user });
    }
  }

  private readStoredSession(): StoredSession | null {
    if (!this.isBrowser()) return null;
    const raw = localStorage.getItem(this.sessionKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredSession;
    } catch {
      return null;
    }
  }

  private readPlatformUser(): AuthUser | null {
    const session = this.readStoredSession();
    if (session?.userType === 'platform') {
      return session.user as AuthUser;
    }
    return null;
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }
}
